// src/components/AdminDashboard.js
import React, { useState, useEffect, useMemo } from "react";
import "../styles/AdminDashboard.css";
import Orders from "./Orders";
import Customers from "./Customers";
import Products from "./ProductManagement/Products";
import Vendors from "./Vendors";
import Sellers from "./Sellers";
import Stores from "./Stores";
import Inventory from "./Inventory";
import SellerApplications from "./SellerApplications"; // ✅ NEW
import config from "../config";
import JoyauraLogo from "../assets/JoyauraLogo.png";

export const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formatMoney = (val) =>
    typeof val === "number" ? `₹${val.toFixed(2)}` : `₹${val || 0}`;

  const getOrderItemCount = (order) =>
    (order.items || []).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const getOrderType = (order) => {
    const source = (order?.orderSource || "").toString().trim().toUpperCase();
    if (source === "OFFLINE") return "OFFLINE";
    if (source === "ASSISTED") return "ASSISTED";
    if (source === "EXHIBITION") return "EXHIBITION";
    return "ONLINE";
  };

  const dashboardStats = useMemo(() => {
    const safeNumber = (value) => Number(value || 0);

    const totalOrders = orders.length;
    const totalItemsSold = orders.reduce(
      (sum, order) => sum + getOrderItemCount(order),
      0
    );
    const totalRevenue = orders.reduce(
      (sum, order) => sum + safeNumber(order.totalAmount),
      0
    );
    const sellerEarnings = orders.reduce((orderSum, order) => {
      const itemEarnings = (order.items || []).reduce(
        (itemSum, item) => itemSum + safeNumber(item.sellerEarning),
        0
      );
      return orderSum + itemEarnings;
    }, 0);
    const auraCommission = orders.reduce((orderSum, order) => {
      const itemCommission = (order.items || []).reduce(
        (itemSum, item) => itemSum + safeNumber(item.commissionAmount),
        0
      );
      return orderSum + itemCommission;
    }, 0);
    const offlineOrders = orders.filter(
      (order) => getOrderType(order) === "OFFLINE"
    ).length;
    const onlineOrders = orders.filter(
      (order) => getOrderType(order) === "ONLINE"
    ).length;
    const offlineRevenue = orders.reduce((sum, order) => {
      return getOrderType(order) === "OFFLINE"
        ? sum + safeNumber(order.totalAmount)
        : sum;
    }, 0);
    const onlineRevenue = orders.reduce((sum, order) => {
      return getOrderType(order) === "ONLINE"
        ? sum + safeNumber(order.totalAmount)
        : sum;
    }, 0);

    return {
      totalOrders,
      totalItemsSold,
      totalRevenue,
      sellerEarnings,
      auraCommission,
      offlineOrders,
      onlineOrders,
      offlineRevenue,
      onlineRevenue,
    };
  }, [orders]);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${config.baseUrl}/api/orders/allorders`);
        if (!response.ok) {
          throw new Error("Failed to fetch dashboard metrics");
        }
        const data = await response.json();
        const ordersData = Array.isArray(data) ? data : [data];
        const sortedOrders = ordersData.sort((a, b) => {
          const dateA = new Date(a.createdDate || 0);
          const dateB = new Date(b.createdDate || 0);
          return dateB - dateA;
        });
        setOrders(sortedOrders);
      } catch (err) {
        console.error(err);
        setError(err.message || "Unable to load dashboard metrics.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  return (
    <div className="dashboard-home-section">
      <div className="section-header">
        <h2>Dashboard</h2>
        <p>Quick metrics for revenue, orders, commission, and online/offline performance.</p>
      </div>
      <div className="orders-dashboard-stats">
        <div className="orders-dashboard-card orders-dashboard-card--default">
          <div className="orders-dashboard-label">Total Orders</div>
          <div className="orders-dashboard-value">{dashboardStats.totalOrders}</div>
        </div>
        <div className="orders-dashboard-card orders-dashboard-card--default">
          <div className="orders-dashboard-label">Total Items Sold</div>
          <div className="orders-dashboard-value">{dashboardStats.totalItemsSold}</div>
        </div>
        <div className="orders-dashboard-card orders-dashboard-card--revenue">
          <div className="orders-dashboard-label">Total Revenue</div>
          <div className="orders-dashboard-value">{formatMoney(dashboardStats.totalRevenue)}</div>
        </div>
        <div className="orders-dashboard-card orders-dashboard-card--commission">
          <div className="orders-dashboard-label">Seller Earnings</div>
          <div className="orders-dashboard-value">{formatMoney(dashboardStats.sellerEarnings)}</div>
        </div>
        <div className="orders-dashboard-card orders-dashboard-card--commission">
          <div className="orders-dashboard-label">AuraKart Commission</div>
          <div className="orders-dashboard-value">{formatMoney(dashboardStats.auraCommission)}</div>
        </div>
        <div className="orders-dashboard-card orders-dashboard-card--offline">
          <div className="orders-dashboard-label">Offline Orders</div>
          <div className="orders-dashboard-value">{dashboardStats.offlineOrders}</div>
        </div>
        <div className="orders-dashboard-card orders-dashboard-card--online">
          <div className="orders-dashboard-label">Online Orders</div>
          <div className="orders-dashboard-value">{dashboardStats.onlineOrders}</div>
        </div>
        <div className="orders-dashboard-card orders-dashboard-card--offline">
          <div className="orders-dashboard-label">Offline Revenue</div>
          <div className="orders-dashboard-value">{formatMoney(dashboardStats.offlineRevenue)}</div>
        </div>
        <div className="orders-dashboard-card orders-dashboard-card--online">
          <div className="orders-dashboard-label">Online Revenue</div>
          <div className="orders-dashboard-value">{formatMoney(dashboardStats.onlineRevenue)}</div>
        </div>
      </div>
      {loading && <p className="loading">Loading metrics...</p>}
      {error && <p className="error">{error}</p>}
    </div>
  );
};

const sections = {
  Dashboard,
  Orders,
  Customers,
  Products,
  Stores,
  Vendors,
  Sellers,
  SellerApplications, // ✅ visible as new tab
  Inventory,
};

export default function AdminDashboard({ onLogout, userRole = "admin" }) {
  const [activeSection, setActiveSection] = useState("Dashboard");
  const username = localStorage.getItem("username") || "User";
  const roleDisplay = userRole === "admin" ? "👨‍💼 Admin" : "🏪 Seller";

  const ActiveComponent = sections[activeSection];

  return (
    <div className="dashboard-container">
      {/* HEADER */}
      <header className="dashboard-header">
        <div className="header-left">
          <img src={JoyauraLogo} alt="Joyaura Logo" className="header-logo" />
          <h1>Admin Dashboard</h1>
        </div>

        <div className="header-right">
          <span className="welcome-text">
            Welcome, {roleDisplay} ({username})
          </span>
          <button className="logout-btn" onClick={onLogout}>
            Logout
          </button>
        </div>
      </header>

      {/* NAVIGATION */}
      <nav className="dashboard-nav">
        <ul className="nav-list">
          {Object.keys(sections).map((section) => (
            <li key={section}>
              <button
                className={`nav-btn ${
                  activeSection === section ? "active" : ""
                }`}
                onClick={() => setActiveSection(section)}
              >
                {section}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* MAIN CONTENT */}
      <main className="dashboard-main">
        {ActiveComponent && <ActiveComponent />}
      </main>
    </div>
  );
}
